import { ReactNode, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useUserSettings } from "../hooks/useUserSettings";

interface ProtectedRouteProps {
  children: ReactNode;
  /**
   * Skip the onboarding completion check.
   * Set to true for routes that should be accessible without completing onboarding
   * (e.g., the onboarding page itself, settings page).
   */
  skipOnboardingCheck?: boolean;
}

/**
 * Wrapper component that requires authentication.
 * Redirects to login if user is not authenticated.
 * Optionally redirects to onboarding if user hasn't completed it.
 */
export function ProtectedRoute({ children, skipOnboardingCheck = false }: ProtectedRouteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isBootstrapping } = useAuth();
  const { data: settings, isLoading: settingsLoading } = useUserSettings();

  useEffect(() => {
    // Wait for auth initialization to complete
    if (isBootstrapping) {
      return; // Still checking auth, don't redirect yet
    }

    // User is not authenticated - redirect to login
    if (!isAuthenticated) {
      const currentPath = location.pathname + location.search;
      navigate(`/login?redirectTo=${encodeURIComponent(currentPath)}`, {
        replace: true, // Replace history to avoid back button issues
      });
      return;
    }

    // Check onboarding status if needed
    if (!skipOnboardingCheck && !settingsLoading && settings) {
      const hasCompletedOnboarding = settings.preferences?.hasCompletedOnboarding === true;

      if (!hasCompletedOnboarding) {
        navigate("/onboarding", { replace: true });
      }
    }
  }, [
    isAuthenticated,
    isBootstrapping,
    navigate,
    location,
    skipOnboardingCheck,
    settingsLoading,
    settings,
  ]);

  // Show loading state while checking auth or settings
  if (isBootstrapping || (!skipOnboardingCheck && settingsLoading)) {
    return (
      <div className="h-screen w-full bg-[var(--color-bg-page)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
          <p className="text-sm text-[var(--color-text-secondary)]">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render protected content until auth is confirmed
  if (!isAuthenticated) {
    return null; // Redirect in progress
  }

  // Don't render if redirecting to onboarding
  if (!skipOnboardingCheck && settings && settings.preferences?.hasCompletedOnboarding !== true) {
    return null; // Redirect to onboarding in progress
  }

  // User is authenticated - render protected content
  return <>{children}</>;
}
